import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Printer, CheckCircle, XCircle, Slash, Package, Truck, FileText, Tag, Calendar, DollarSign, Hash } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetLpoQuery,
  useGetLpoRevisionsQuery,
  useApproveLpoMutation,
  useRejectLpoMutation,
  useCancelLpoMutation,
} from "../../stockApi";
import { useGetQuotationQuery } from "../../../sales/quotations/quotationsApi";
import { useGetMaintenanceRequestQuery } from "../../../masters/vehicles/vehiclesApi";
import VehicleRequestDetailCard from "../../../masters/vehicles/components/VehicleRequestDetailCard";
import QuotationLinkedPanel from "../../../sales/quotations/components/QuotationLinkedPanel";
import { useQuotationGroupCheck } from "../useQuotationGroupCheck";
import LinkedDocumentsPanel from "../../components/LinkedDocumentsPanel";
import {
  useCanEditLpo,
  useCanEditApprovedLpo,
  useCanApproveLpo,
  useCanCancelLpo,
  useCanCreateLpo,
  useCanCreateStockIn,
  useCanPrintLpo,
} from "../../useStockPermissions";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import StockDocumentPrintModal from "../../components/print/StockDocumentPrintModal";
import LpoAttachments from "../../components/LpoAttachments";
import { LpoCycleStrip } from "../lpoCycle";
import { paths } from "../../../../routes/paths";
import { useGetCompanyInfoQuery } from "../../../settings/settingsApi";
import { useLazyGetEmployeeSignatureQuery } from "../../../masters/employees/employeesApi";
import { buildLpoPrintProps, blobToDataUrl, LPO_PRINTABLE_STATUSES } from "../buildLpoPrintProps";

const EDITABLE_STATUSES = ["SUBMITTED", "REVISED_SUBMITTED", "REJECTED"];
const APPROVABLE_STATUSES = ["SUBMITTED", "REVISED_SUBMITTED"];
const STOCK_IN_STATUSES = ["APPROVED", "PARTIALLY_RECEIVED"];

const LpoDetail = () => {
  const { recordGuid } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanEditLpo();
  const canEditApproved = useCanEditApprovedLpo();
  const canApprove = useCanApproveLpo();
  const canCancel = useCanCancelLpo();
  const canCreate = useCanCreateLpo();
  const canCreateStockIn = useCanCreateStockIn();
  const canPrint = useCanPrintLpo();

  const { data: lpo, isLoading, isError, error } = useGetLpoQuery(recordGuid);
  const { data: revisions = [] } = useGetLpoRevisionsQuery(recordGuid);
  const { data: companyInfo } = useGetCompanyInfoQuery(lpo?.branchId, { skip: !lpo });
  // Only fetched when this LPO was actually created against an approved
  // Quotation (Phase 2 linking) - full detail (not just the number already
  // on the LPO record) so the total amount can show in the summary line and
  // the expandable panel below has line items to show without a second call.
  const { data: linkedQuotation } = useGetQuotationQuery(lpo?.quotationRecordGuid, { skip: !lpo?.quotationRecordGuid });
  // Batch185 - the same group check the LPO form runs, so whoever approves sees
  // the colours the person raising it saw. The hook works on editor-shaped rows.
  const groupCheckRows = React.useMemo(
    () => (lpo?.lines || []).map((l) => ({ key: `line-${l.lineId}`, productCode: l.productCode, qty: l.orderedQty })),
    [lpo]
  );
  const { rowFlags: groupFlags } = useQuotationGroupCheck({
    quotationRecordGuid: lpo?.quotationRecordGuid,
    branchId: lpo?.branchId,
    rows: groupCheckRows,
  });
  // Only fetched to pull RegNo/Brand/Model for the printout - the LPO itself
  // only carries the request's number, not the vehicle's own details.
  const { data: linkedVehicleRequest } = useGetMaintenanceRequestQuery(lpo?.vehicleRequestRecordGuid, { skip: !lpo?.vehicleRequestRecordGuid });
  const [approveLpo] = useApproveLpoMutation();
  const [rejectLpo] = useRejectLpoMutation();
  const [cancelLpo] = useCancelLpoMutation();

  const [showPrint, setShowPrint] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);

  // The approver's seal/signature ("Approval Signature" Employee Document,
  // Batch92) - fetched once the LPO carries an approver's EmployeeRecordGuid
  // and converted to a data: URI up front, same reasoning as the print
  // popup being unable to carry auth headers itself (see buildLpoPrintProps).
  const [fetchSignature] = useLazyGetEmployeeSignatureQuery();
  const [signatureImageUrl, setSignatureImageUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!lpo?.approvedByEmployeeRecordGuid) {
      setSignatureImageUrl(null);
      return undefined;
    }
    fetchSignature(lpo.approvedByEmployeeRecordGuid)
      .unwrap()
      .then(blobToDataUrl)
      .then((url) => { if (!cancelled) setSignatureImageUrl(url); })
      .catch(() => { if (!cancelled) setSignatureImageUrl(null); });
    return () => { cancelled = true; };
  }, [lpo?.approvedByEmployeeRecordGuid, fetchSignature]);

  const handleApprove = async () => {
    try {
      await approveLpo(recordGuid).unwrap();
      toast.success("LPO approved");
    } catch (e) {
      toast.error(e?.message || "Could not approve the LPO");
    }
  };

  const handleReject = async (rejectionReason) => {
    if (!rejectionReason.trim()) return toast.error("A rejection reason is required");
    try {
      await rejectLpo({ recordGuid, rejectionReason }).unwrap();
      toast.success("LPO rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the LPO");
    }
  };

  const handleCancel = async (cancelReason) => {
    try {
      await cancelLpo({ recordGuid, cancelReason: cancelReason || null }).unwrap();
      toast.success("LPO cancelled");
      setShowCancelForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not cancel the LPO");
    }
  };

  if (isLoading) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }
  if (isError || !lpo) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to LPOs
          </Link>
          <div className="alert alert-danger">Could not load this LPO ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  const vehicleInfo = linkedVehicleRequest
    ? { regNo: linkedVehicleRequest.regNo, brand: linkedVehicleRequest.brand, model: linkedVehicleRequest.model }
    : undefined;
  const printProps = buildLpoPrintProps(lpo, companyInfo, signatureImageUrl, vehicleInfo);
  const canPrintNow = canPrint && LPO_PRINTABLE_STATUSES.includes(lpo.status);

  // Collapsed-by-default summary rows for whatever this LPO was raised
  // against - a Vehicle Maintenance Request and/or an approved Quotation.
  // The "amount" chip is deliberately the LPO's own Net Amount for the
  // vehicle row (what this maintenance actually cost) and the Quotation's
  // own Total for the quotation row (what was quoted), not the same number
  // relabelled twice.
  const linkedDocItems = [];
  if (lpo.vehicleRequestRecordGuid) {
    linkedDocItems.push({
      key: "vehicle",
      icon: Truck,
      title: "Vehicle Maintenance Request",
      chips: linkedVehicleRequest
        ? [
            { label: linkedVehicleRequest.regNo, icon: Tag },
            { label: `${linkedVehicleRequest.brand} ${linkedVehicleRequest.model}`, icon: Truck },
            { label: new Date(linkedVehicleRequest.requestDate).toLocaleDateString(), icon: Calendar },
            { label: Number(lpo.netAmount).toFixed(2), icon: DollarSign },
          ]
        : [lpo.vehicleRequestNumber],
      detail: <VehicleRequestDetailCard request={linkedVehicleRequest} />,
    });
  }
  if (lpo.quotationRecordGuid) {
    linkedDocItems.push({
      key: "quotation",
      icon: FileText,
      title: "Quotation",
      chips: linkedQuotation
        ? [
            { label: linkedQuotation.quotationNumber, icon: Hash },
            { label: new Date(linkedQuotation.quotationDate).toLocaleDateString(), icon: Calendar },
            { label: Number(linkedQuotation.totalAmount).toFixed(2), icon: DollarSign },
          ]
        : [lpo.quotationNumber],
      detail: linkedQuotation ? <QuotationLinkedPanel quotation={linkedQuotation} bare /> : <p className="text-muted mb-0">Loading...</p>,
    });
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.stockLpos} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to LPOs
              </Link>
              <h4>{lpo.lpoNumber} <StatusBadge status={lpo.status} /></h4>
              <h6>{lpo.branchCode} - {lpo.purposeType.replaceAll("_", " ")}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2 flex-wrap">
            {canPrintNow && (
              <button type="button" className="btn btn-outline-primary d-inline-flex align-items-center gap-1" onClick={() => setShowPrint(true)}>
                <Printer size={16} /> Print
              </button>
            )}
            {((canEdit && EDITABLE_STATUSES.includes(lpo.status)) || (canEditApproved && lpo.status === "APPROVED")) && (
              <button type="button" className="btn btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => navigate(paths.stockLpoEdit(recordGuid))}>
                <Edit size={16} /> Edit
              </button>
            )}
            {canApprove && APPROVABLE_STATUSES.includes(lpo.status) && (
              <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleApprove}>
                <CheckCircle size={16} /> Approve
              </button>
            )}
            {canApprove && APPROVABLE_STATUSES.includes(lpo.status) && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                <XCircle size={16} /> Reject
              </button>
            )}
            {canCancel && !["CANCELLED", "FULLY_RECEIVED"].includes(lpo.status) && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowCancelForm((s) => !s)}>
                <Slash size={16} /> Cancel
              </button>
            )}
            {canCreateStockIn && STOCK_IN_STATUSES.includes(lpo.status) && (
              <button
                type="button"
                className="btn btn-added d-inline-flex align-items-center gap-1"
                onClick={() => navigate(paths.stockInNew, { state: { fromLpo: { recordGuid: lpo.recordGuid, lpoNumber: lpo.lpoNumber } } })}
              >
                <Package size={16} /> Create Stock-In
              </button>
            )}
          </div>
        </div>

        {showRejectForm && (
          <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />
        )}
        {showCancelForm && (
          <ReasonPrompt label="Cancel Reason (optional)" confirmLabel="Cancel LPO" onConfirm={handleCancel} onCancel={() => setShowCancelForm(false)} />
        )}

        {/* Submission -> approval -> received -> invoice -> paid, with the evidence for each
            (Batch199). Invoice and cheque are attached below; a cash LPO's payment is its
            petty cash voucher. */}
        <LpoCycleStrip lpo={lpo} />

        {lpo.vehicleRequestRecordGuid && (
          <div className="alert alert-success">
            Linked to Vehicle Maintenance Request <strong>{lpo.vehicleRequestNumber}</strong>
          </div>
        )}
        {lpo.status === "REJECTED" && lpo.rejectionReason && (
          <div className="alert alert-danger">Rejected by {lpo.rejectedByName}: {lpo.rejectionReason}</div>
        )}
        {lpo.status === "CANCELLED" && (
          <div className="alert alert-secondary">Cancelled by {lpo.cancelledByName}{lpo.cancelReason ? `: ${lpo.cancelReason}` : ""}</div>
        )}
        {lpo.lastEditedAfterApprovalBy && (
          <div className="alert alert-warning">
            Edited after approval by {lpo.lastEditedAfterApprovalByName} on {new Date(lpo.lastEditedAfterApprovalDate).toLocaleString()}
            {lpo.postApprovalEditCount > 1 ? ` (${lpo.postApprovalEditCount} post-approval edits total)` : ""}.
            See Revision History below for what changed and why.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-3"><strong>Supplier:</strong> {lpo.supplierName}</div>
              <div className="col-md-2"><strong>Date:</strong> {new Date(lpo.lpoDate).toLocaleDateString()}</div>
              <div className="col-md-2"><strong>Ref No:</strong> {lpo.refNo || "-"}</div>
              <div className="col-md-2"><strong>Purpose:</strong> {lpo.purposeType.replaceAll("_", " ")}</div>
              <div className="col-md-2"><strong>Payment Type:</strong> {lpo.paymentType === "CREDIT" ? "Credit" : "Cash"}</div>
              <div className="col-md-3"><strong>Customer:</strong> {lpo.customerName || "-"}</div>
              {lpo.quotationRecordGuid && (
                <div className="col-md-3"><strong>Quotation:</strong> {lpo.quotationNumber}</div>
              )}
              {lpo.purposeDescription && <div className="col-md-12 mt-2"><strong>Description:</strong> {lpo.purposeDescription}</div>}
              {lpo.remarks && <div className="col-md-12 mt-2"><strong>Remarks:</strong> {lpo.remarks}</div>}
            </div>

            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th><th>Product</th><th>Ordered Qty</th><th>Received Qty</th><th>Unit</th><th>Rate</th><th>VAT %</th><th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lpo.lines.map((l, idx) => (
                    <tr key={l.lineId} className={groupFlags?.[`line-${l.lineId}`] ? `table-${groupFlags[`line-${l.lineId}`].tone}` : undefined}>
                      <td>{idx + 1}</td>
                      <td>
                        {l.productName}
                        {groupFlags?.[`line-${l.lineId}`] && (
                          <div className={`small fw-semibold text-${groupFlags[`line-${l.lineId}`].tone === "danger" ? "danger" : "dark"}`}>
                            {groupFlags[`line-${l.lineId}`].text}
                          </div>
                        )}
                      </td>
                      <td>{l.orderedQty}</td>
                      <td>{l.receivedQty}</td>
                      <td>{l.unit}</td>
                      <td>{Number(l.unitRate).toFixed(2)}</td>
                      <td>{l.vatPercent}</td>
                      <td>{Number(l.lineTotal).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end gap-4 mt-2">
              <div>Sub Total: <strong>{Number(lpo.subTotal).toFixed(2)}</strong></div>
              <div>VAT: <strong>{Number(lpo.vatAmount).toFixed(2)}</strong></div>
              <div>Discount: <strong>{Number(lpo.discountAmount).toFixed(2)}</strong></div>
              <div>Net Amount: <strong>{Number(lpo.netAmount).toFixed(2)}</strong></div>
            </div>

            <div className="text-muted small mt-3">
              Created by <ActorName action="Created" name={lpo.createdByName} /> on {new Date(lpo.createdDate).toLocaleString()}
              {lpo.approvedByName && (
                <> - Approved by <ActorName action="Approved" name={lpo.approvedByName} /> on {new Date(lpo.approvedDate).toLocaleString()}</>
              )}
            </div>
          </div>
        </div>

        <LpoAttachments recordGuid={lpo.recordGuid} attachments={lpo.attachments} canManage={canCreate} lpo={lpo} />

        <LinkedDocumentsPanel items={linkedDocItems} />

        {revisions.length > 0 && (
          <div className="card mb-4">
            <div className="card-body">
              <h5 className="mb-3">Revision History</h5>
              <ul className="list-unstyled mb-0">
                {revisions.map((r) => (
                  <li key={r.revisionId} className="mb-2">
                    <strong>Rev {r.revisionNumber}</strong> by {r.createdByName} on {new Date(r.createdDate).toLocaleString()}: {r.changeSummary}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <StockDocumentPrintModal
          show={showPrint}
          onClose={() => setShowPrint(false)}
          docProps={printProps}
          copies={2}
          enableEmail
          lpoRecordGuid={lpo.recordGuid}
        />
      </div>
    </div>
  );
};

export default LpoDetail;
